import type React from 'react';
import type { IconName } from '../primitives/Icon';
import { ScrollView } from 'react-native';
import { XStack, YStack } from 'tamagui';
import { colors } from '../theme/colors';
import { IconButton } from '../primitives/IconButton';

export interface ToolbarAction {
  /** Stable identifier — used as React key. */
  id: string;
  iconName: IconName;
  /** Accessibility label (and tooltip on web). */
  label: string;
  onPress: () => void;
  /** Highlights the button (e.g. bold currently active in a markdown editor). */
  active?: boolean | undefined;
  disabled?: boolean | undefined;
}

export interface ToolbarProps {
  actions: ToolbarAction[];
  /** How to handle horizontal overflow. Default 'scroll'. */
  overflowMode?: 'scroll' | 'wrap' | undefined;
  /** Optional content rendered after the action row — e.g. inline picker
   *  popovers (tag dropdown, emoji keyboard) opened by an action. */
  trailing?: React.ReactNode | undefined;
}

/**
 * Generic horizontal action bar. Used by note formatting toolbars and any
 * other surface that needs a row of icon buttons.
 *
 * Per ADR-0079 / Phase 6, each button is the shared `IconButton`
 * primitive — guarantees the 44pt touch-target floor via `useHitSlop`
 * and required `accessibilityLabel` at the type level. Replaces the
 * prior hand-rolled Pressable + MaterialIcons + `hitSlop={6}` pattern.
 */
export function Toolbar({ actions, overflowMode = 'scroll', trailing }: ToolbarProps) {
  const buttons = (
    <XStack
      gap="$1"
      paddingVertical="$1"
      flexWrap={overflowMode === 'wrap' ? 'wrap' : 'nowrap'}
      role="toolbar"
    >
      {actions.map((action) => (
        <IconButton
          key={action.id}
          iconName={action.iconName}
          onPress={action.onPress}
          accessibilityLabel={action.label}
          size="sm"
          {...(action.disabled !== undefined ? { disabled: action.disabled } : null)}
          // `selected` mirrors `action.active` so screen readers
          // announce the toggle state (e.g., "Bold, selected") via
          // accessibilityState.selected + aria-pressed.
          {...(action.active !== undefined ? { selected: action.active } : null)}
          // Active toolbar buttons paint a muted background.
          {...(action.active
            ? { background: colors.borderMuted, foreground: colors.accentCerulean }
            : {})}
        />
      ))}
    </XStack>
  );

  return (
    <YStack gap="$2">
      {overflowMode === 'scroll' ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {buttons}
        </ScrollView>
      ) : (
        buttons
      )}
      {trailing}
    </YStack>
  );
}
